import type { ToolDefinition } from './types';
import { targetSizeTemplate, compressionSynonymTemplate, useCaseTemplate, cropTemplate, rotateTemplate, resizePercentTemplate, batchTemplate, adjustTemplate, imagePdfTemplate, targetSizeUseCaseTemplate, resizeUseCaseTemplate } from './templates';
import {
  phase1TargetSizeConfigs,
  phase1CompressionConfigs,
  phase1UseCaseConfigs,
  phase2CropConfigs,
  phase2RotateConfigs,
  phase2ResizePercentConfigs,
  phase2BatchConfigs,
  phase3AdjustConfigs,
  phase3PdfConfigs,
  phase3AdvancedConvertConfigs,
  phase4TargetUseCaseConfigs,
  phase4AdjustConfigs,
  phase4ResizeConfigs,
  type TargetSizeConfig,
  type CompressionConfig,
  type UseCaseConfig,
  type CropConfig,
  type RotateConfig,
  type ResizePercentConfig,
  type BatchConfig,
  type AdjustConfig,
  type PdfConfig,
  type AdvancedConvertConfig,
  type Phase4TargetUseCaseConfig,
  type Phase4ResizeConfig,
} from './configs';

function generateTargetSizeTool(config: TargetSizeConfig): ToolDefinition {
  const content = targetSizeTemplate(config.size, config.keyword);
  const kw = config.keyword === 'foto' ? 'foto' : 'imagen';
  const sizeNum = parseFloat(config.size);
  const unit = config.size.toUpperCase().includes('MB') ? 'MB' : 'KB';
  const isSmall = unit === 'KB' && sizeNum <= 75;
  const exampleOriginal = isSmall ? '500KB' : unit === 'MB' ? `${sizeNum * 3}MB` : `${Math.round(sizeNum * 0.02)}MB`;
  const exampleFinal = isSmall ? `${Math.round(sizeNum * 0.95)}KB` : unit === 'MB' ? `${(sizeNum * 0.95).toFixed(1)}MB` : `${Math.round(sizeNum * 0.95)}KB`;

  return {
    ...content,
    slug: config.slug,
    pageType: 'target-size',
    searchIntent: 'reduce-size',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'target-size',
    targetSize: config.size,
    relatedSlugs: [],
    h1: `Reduce tu ${kw} a ${config.size} en segundos`,
    subtitle: `Sube tu ${kw} y la optimizamos automáticamente para que pese menos de ${config.size}.`,
    uploadButtonLabel: `Sube tu ${kw} y redúcela a ${config.size}`,
    showBeforeAfterSlider: true,
    trustBullets: ['Sin perder calidad visible', 'Funciona con JPG, PNG y WebP', 'Procesamiento en tu navegador'],
    exampleReductionText: `Ejemplo: ${exampleOriginal} → ${exampleFinal}`,
    processingText: `Reduciendo ${kw} a ${config.size}...`,
    useCases: isSmall ? ['firmas digitales', 'avatares', 'formularios'] : ['formularios', 'email', 'web'],
    nextTools: [
      { slug: 'comprimir-imagen', label: 'Comprimir imagen' },
      { slug: 'convertir-jpg-webp', label: 'Convertir a WebP' },
      { slug: 'redimensionar-imagen', label: 'Redimensionar imagen' },
    ],
  };
}

function generateCompressionTool(config: CompressionConfig): ToolDefinition {
  const content = compressionSynonymTemplate(config.keyword, config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'compression',
    searchIntent: 'compress',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'compression',
    relatedSlugs: [],
  };
}

function generateUseCaseTool(config: UseCaseConfig): ToolDefinition {
  const content = useCaseTemplate(config.useCase, config.context, config.detailText);
  return {
    ...content,
    slug: config.slug,
    pageType: config.pageType,
    searchIntent: config.pageType === 'optimization' ? 'optimize' : 'compress',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'compression',
    relatedSlugs: [],
  };
}

function generateCropTool(config: CropConfig): ToolDefinition {
  const content = cropTemplate(config.aspectRatio, config.platform);
  return {
    ...content,
    slug: config.slug,
    pageType: 'crop',
    searchIntent: 'crop',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'crop',
    aspectRatio: config.aspectRatio,
    platform: config.platform,
    relatedSlugs: [],
  };
}

function generateRotateTool(config: RotateConfig): ToolDefinition {
  const content = rotateTemplate(config.keyword, config.displayKeyword, config.rotatePreset);
  return {
    ...content,
    slug: config.slug,
    pageType: 'rotate',
    searchIntent: 'rotate',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'rotate',
    rotatePreset: config.rotatePreset,
    relatedSlugs: [],
  };
}

function generateResizePercentTool(config: ResizePercentConfig): ToolDefinition {
  const content = resizePercentTemplate(config.percent, config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'resize-percent',
    searchIntent: 'resize',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'resize-percent',
    resizePercent: config.percent,
    relatedSlugs: [],
  };
}

function generateBatchTool(config: BatchConfig): ToolDefinition {
  const content = batchTemplate(config.displayKeyword, 'comprime');
  return {
    ...content,
    slug: config.slug,
    pageType: 'batch',
    searchIntent: 'batch',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'batch',
    batchToolType: config.batchToolType,
    relatedSlugs: [],
  };
}

function generateAdjustTool(config: AdjustConfig): ToolDefinition {
  const content = adjustTemplate(config.preset, config.displayKeyword);
  const tool: ToolDefinition = {
    ...content,
    slug: config.slug,
    pageType: 'adjust',
    searchIntent: 'adjust',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'adjust',
    adjustPreset: config.preset,
    relatedSlugs: [],
  };

  // UX overrides for suavizar-imagen
  if (config.slug === 'suavizar-imagen') {
    tool.uploadTitle = 'Suaviza tu imagen en segundos';
    tool.uploadSubtitle = 'Reduce ruido y mejora la apariencia sin perder calidad';
    tool.uploadButtonLabel = 'Sube tu imagen y suavízala';
    tool.helperText = 'Esta herramienta reduce el ruido y suaviza los detalles de la imagen, ideal para fotos granuladas o de baja calidad.';
    tool.showBeforeAfterSlider = true;
    tool.crossSellSlug = 'comprimir-imagen';
    tool.crossSellText = '¿Quieres reducir también el tamaño del archivo?';
    tool.crossSellButtonText = 'Comprimir imagen';
  }

  if (config.slug === 'mejorar-calidad-imagen') {
    tool.uploadTitle = 'Mejora la calidad de tu imagen';
    tool.uploadSubtitle = 'Aumenta nitidez y claridad en segundos';
    tool.uploadButtonLabel = 'Sube tu imagen y mejórala';
    tool.helperText = 'Ideal para fotos borrosas, oscuras o con baja calidad.';
    tool.showBeforeAfterSlider = true;
    tool.crossSellSlug = 'comprimir-imagen';
    tool.crossSellText = '¿Quieres optimizar también el tamaño?';
    tool.crossSellButtonText = 'Comprimir imagen';
  }

  return tool;
}

function generatePdfTool(config: PdfConfig): ToolDefinition {
  const content = imagePdfTemplate(config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'image-to-pdf',
    searchIntent: 'convert-pdf',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'image-to-pdf',
    pdfMode: 'image-to-pdf',
    relatedSlugs: [],
  };
}

function generateAdvancedConvertTool(config: AdvancedConvertConfig): ToolDefinition {
  const displayKeyword = `convertir ${config.sourceFormat} a ${config.targetFormat}`;
  const content = compressionSynonymTemplate(config.slug, displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'conversion',
    searchIntent: 'convert',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'conversion',
    sourceFormat: config.sourceFormat,
    targetFormat: config.targetFormat,
    relatedSlugs: [],
  };
}

export function generatePhase1Tools(): ToolDefinition[] {
  return [
    ...phase1TargetSizeConfigs.map(generateTargetSizeTool),
    ...phase1CompressionConfigs.map(generateCompressionTool),
    ...phase1UseCaseConfigs.map(generateUseCaseTool),
  ];
}

export function generatePhase2Tools(): ToolDefinition[] {
  return [
    ...phase2CropConfigs.map(generateCropTool),
    ...phase2RotateConfigs.map(generateRotateTool),
    ...phase2ResizePercentConfigs.map(generateResizePercentTool),
    ...phase2BatchConfigs.map(generateBatchTool),
  ];
}

export function generatePhase3Tools(): ToolDefinition[] {
  return [
    ...phase3AdjustConfigs.map(generateAdjustTool),
    ...phase3PdfConfigs.map(generatePdfTool),
    ...phase3AdvancedConvertConfigs.map(generateAdvancedConvertTool),
  ];
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 4 generators — target-size use-case, adjust intents, resize use-case
// ═══════════════════════════════════════════════════════════════════════════

function generatePhase4TargetUseCaseTool(config: Phase4TargetUseCaseConfig): ToolDefinition {
  const content = targetSizeUseCaseTemplate(config.size, config.context, config.detailText);
  const tool: ToolDefinition = {
    ...content,
    slug: config.slug,
    pageType: 'target-size',
    searchIntent: 'reduce-size',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'target-size',
    targetSize: config.size,
    relatedSlugs: [],
  };

  // UX overrides per slug
  if (config.slug === 'reducir-imagen-para-cv') {
    tool.uploadTitle = 'Reduce tu foto para el CV';
    tool.uploadSubtitle = 'Ajusta el peso al límite que exige el portal de empleo';
    tool.helperText = 'La mayoría de portales aceptan fotos de hasta 200KB. Ajusta el peso según los requisitos específicos de la plataforma.';
    tool.showBeforeAfterSlider = true;
    tool.crossSellSlug = 'redimensionar-imagen-para-cv';
    tool.crossSellText = '¿Necesitas también ajustar las dimensiones de la foto?';
    tool.crossSellButtonText = 'Redimensionar para CV';
  }

  return tool;
}

function generatePhase4AdjustTool(config: AdjustConfig): ToolDefinition {
  const content = adjustTemplate(config.preset, config.displayKeyword);
  const tool: ToolDefinition = {
    ...content,
    slug: config.slug,
    pageType: 'adjust',
    searchIntent: 'adjust',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'adjust',
    adjustPreset: config.preset,
    relatedSlugs: [],
    showBeforeAfterSlider: true,
    crossSellSlug: 'comprimir-imagen',
    crossSellText: '¿Quieres reducir también el tamaño del archivo?',
    crossSellButtonText: 'Comprimir imagen',
  };

  // Differentiated UX copy per tool
  const uxOverrides: Record<string, { uploadTitle: string; uploadSubtitle: string; uploadButtonLabel: string; helperText: string }> = {
    'quitar-ruido-imagen': {
      uploadTitle: 'Elimina el ruido digital de tus fotos',
      uploadSubtitle: 'Reduce el ruido causado por poca luz o ISO alto',
      uploadButtonLabel: 'Sube tu imagen y quita el ruido',
      helperText: 'Ideal para fotos tomadas en condiciones de poca luz, con ruido digital visible o granulado por sensores de alta sensibilidad.',
    },
    'eliminar-grano-imagen': {
      uploadTitle: 'Elimina el grano de tus fotografías',
      uploadSubtitle: 'Suaviza la textura granulada de tus fotos',
      uploadButtonLabel: 'Sube tu imagen y elimina el grano',
      helperText: 'Perfecto para fotografías con textura de grano visible, ya sea por condiciones de captura o estilo de la cámara.',
    },
    'mejorar-foto-borrosa': {
      uploadTitle: 'Mejora tu foto borrosa',
      uploadSubtitle: 'Aumenta la nitidez y claridad de fotos desenfocadas',
      uploadButtonLabel: 'Sube tu foto borrosa y mejórala',
      helperText: 'Combina enfoque y mejora automática para restaurar fotos ligeramente borrosas o desenfocadas.',
    },
    'aclarar-imagen-oscura': {
      uploadTitle: 'Aclara tu imagen oscura',
      uploadSubtitle: 'Aumenta brillo y contraste para rescatar fotos con poca luz',
      uploadButtonLabel: 'Sube tu imagen oscura y aclárala',
      helperText: 'Ideal para fotos tomadas en interiores, de noche o con iluminación insuficiente.',
    },
    'mejorar-calidad-foto-online': {
      uploadTitle: 'Mejora la calidad de tu foto online',
      uploadSubtitle: 'Optimiza brillo, contraste y saturación automáticamente',
      uploadButtonLabel: 'Sube tu foto y mejórala',
      helperText: 'Aplica mejoras automáticas de brillo, contraste y saturación para obtener una foto más viva y profesional.',
    },
  };

  const overrides = uxOverrides[config.slug];
  if (overrides) {
    tool.uploadTitle = overrides.uploadTitle;
    tool.uploadSubtitle = overrides.uploadSubtitle;
    tool.uploadButtonLabel = overrides.uploadButtonLabel;
    tool.helperText = overrides.helperText;
  }

  return tool;
}

function generatePhase4ResizeTool(config: Phase4ResizeConfig): ToolDefinition {
  const content = resizeUseCaseTemplate(config.platform, config.dimensions, config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'resize',
    searchIntent: 'resize',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'resize',
    platform: config.platform,
    dimensions: config.dimensions,
    relatedSlugs: [],
  };
}

export function generatePhase4Tools(): ToolDefinition[] {
  return [
    ...phase4TargetUseCaseConfigs.map(generatePhase4TargetUseCaseTool),
    ...phase4AdjustConfigs.map(generatePhase4AdjustTool),
    ...phase4ResizeConfigs.map(generatePhase4ResizeTool),
  ];
}
